import { useState } from 'react';
import { ChevronLeft, ChevronRight, Home, Book } from 'lucide-react';
import { Link } from 'wouter';

export default function PharmaceuticalsRepurposedMedications() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    // Slide 1: Title Slide
    {
      title: "Pharmaceuticals and Repurposed Medications for Longevity",
      subtitle: "A Functional Medicine and Health Optimization Approach for Clinicians",
      content: (
        <div className="text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-blue-800">
              Pharmaceuticals and Repurposed Medications for Longevity
            </h1>
            <h2 className="text-xl text-gray-700">
              A Functional Medicine and Health Optimization Approach for Clinicians
            </h2>
            <p className="text-lg text-gray-600">
              Extending Healthspan Through Evidence-Based Interventions
            </p>
          </div>
          
          {/* Professional Medical Symbol */}
          <div className="flex justify-center">
            <svg width="200" height="120" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="medGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1E40AF" />
                  <stop offset="100%" stopColor="#3B82F6" />
                </linearGradient>
              </defs>
              <circle cx="100" cy="60" r="50" fill="url(#medGrad)" opacity="0.1"/>
              <path d="M85,35 L115,35 L115,45 L125,45 L125,75 L115,75 L115,85 L85,85 L85,75 L75,75 L75,45 L85,45 Z" 
                    fill="url(#medGrad)" stroke="#1E40AF" strokeWidth="2"/>
              <text x="100" y="105" fontFamily="Arial" fontSize="12" textAnchor="middle" fill="#1E40AF">
                Longevity Medicine
              </text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 2: Introduction - The Shift in Modern Medicine
    {
      title: "The Longevity Paradigm: Beyond Lifespan to Healthspan",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <p className="text-sm"><strong>Traditional Focus:</strong> Extending Lifespan (total years lived).</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <p className="text-sm"><strong>Modern Longevity Medicine:</strong> Prioritizing Healthspan – the period of life in good health, free from chronic disease and disability.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
              <p className="text-sm"><strong>The Clinician's Goal:</strong> Not just adding years to life, but life to years.</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
              <p className="text-sm"><strong>Aging as a Risk Factor:</strong> Aging is the single greatest risk factor for major chronic diseases (CVD, T2D, neurodegeneration, cancer).</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
              <p className="text-sm"><strong>Proactive Approach:</strong> Targeting fundamental aging mechanisms to delay or prevent multiple chronic conditions simultaneously, compressing morbidity.</p>
            </div>
          </div>

          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 text-center">
            <p className="text-sm font-medium text-indigo-800">
              <strong>Key Takeaway:</strong> Longevity medicine aims to enhance physiological resilience and function across an extended lifespan.
            </p>
          </div>
        </div>
      )
    },

    // Slide 3: Functional Medicine Framework
    {
      title: "Functional Medicine Principles in Aging: Addressing Root Causes",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Conventional vs. Functional:</h4>
              <div className="space-y-3">
                <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
                  <p className="text-sm"><strong>Conventional:</strong> Often diagnoses and treats established diseases in isolation.</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-400">
                  <p className="text-sm"><strong>Functional:</strong> Seeks to identify and address underlying root causes of dysfunction and imbalance.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Patient-Centered Approach:</h4>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm">Emphasizes genetic predispositions, biochemical uniqueness, lifestyle, and environmental exposures.</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800">Alignment with Geroscience:</h4>
            <div className="space-y-3">
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm">Geroscience posits that targeting fundamental biological mechanisms of aging can collectively delay, prevent, or ameliorate age-related diseases.</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-sm">Recognizes shared underlying aging pathways for many chronic diseases.</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm"><strong>Clinical Application:</strong> Develop personalized strategies to optimize physiological function by addressing these fundamental drivers.</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm font-medium text-blue-800">
              <strong>Key Takeaway:</strong> Functional medicine provides a systems-biology approach to understand and intervene in the aging process.
            </p>
          </div>
        </div>
      )
    },

    // Slide 4: The Hallmarks of Aging
    {
      title: "The Hallmarks of Aging: Understanding the Drivers of Decline",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm">A conceptual framework delineating primary biological drivers of aging (López-Otín et al., 2013, updated 2023). These hallmarks are interconnected and serve as targets for longevity interventions.</p>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800">Key Hallmarks Include:</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-red-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Genomic Instability</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Telomere Attrition</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Epigenetic Alterations</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Loss of Proteostasis</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Deregulated Nutrient-Sensing</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Mitochondrial Dysfunction</p>
              </div>
              <div className="bg-pink-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Cellular Senescence</p>
              </div>
              <div className="bg-indigo-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Stem Cell Exhaustion</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center">
                <p className="text-sm font-medium">Altered Intercellular Communication</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" className="w-80 h-80">
              <circle cx="200" cy="200" r="180" fill="none" stroke="#ccc" strokeWidth="1"/>
              <text x="200" y="200" textAnchor="middle" fontSize="16" fill="#333">Aging Process</text>
              {/* Genomic Instability */}
              <path d="M 200 20 A 180 180 0 0 1 324.8 75.2 L 200 200 Z" fill="#fee2e2" stroke="#333" strokeWidth="0.5"/>
              <text x="270" y="50" textAnchor="middle" fontSize="9" fill="#000">Genomic Instability</text>
              {/* Telomere Attrition */}
              <path d="M 324.8 75.2 A 180 180 0 0 1 380 200 L 200 200 Z" fill="#fed7aa" stroke="#333" strokeWidth="0.5"/>
              <text x="350" y="130" textAnchor="middle" fontSize="9" fill="#000">Telomere Attrition</text>
              {/* Epigenetic Alterations */}
              <path d="M 380 200 A 180 180 0 0 1 324.8 324.8 L 200 200 Z" fill="#fef3c7" stroke="#333" strokeWidth="0.5"/>
              <text x="350" y="270" textAnchor="middle" fontSize="9" fill="#000">Epigenetic Alterations</text>
              {/* Loss of Proteostasis */}
              <path d="M 324.8 324.8 A 180 180 0 0 1 200 380 L 200 200 Z" fill="#d1fae5" stroke="#333" strokeWidth="0.5"/>
              <text x="270" y="350" textAnchor="middle" fontSize="9" fill="#000">Loss of Proteostasis</text>
              {/* Deregulated Nutrient-Sensing */}
              <path d="M 200 380 A 180 180 0 0 1 75.2 324.8 L 200 200 Z" fill="#dbeafe" stroke="#333" strokeWidth="0.5"/>
              <text x="130" y="350" textAnchor="middle" fontSize="9" fill="#000">Deregulated Nutrient-Sensing</text>
              {/* Mitochondrial Dysfunction */}
              <path d="M 75.2 324.8 A 180 180 0 0 1 20 200 L 200 200 Z" fill="#e9d5ff" stroke="#333" strokeWidth="0.5"/>
              <text x="50" y="270" textAnchor="middle" fontSize="9" fill="#000">Mitochondrial Dysfunction</text>
              {/* Cellular Senescence */}
              <path d="M 20 200 A 180 180 0 0 1 75.2 75.2 L 200 200 Z" fill="#fce7f3" stroke="#333" strokeWidth="0.5"/>
              <text x="50" y="130" textAnchor="middle" fontSize="9" fill="#000">Cellular Senescence</text>
              {/* Stem Cell Exhaustion */}
              <path d="M 75.2 75.2 A 180 180 0 0 1 140 40 L 200 200 Z" fill="#e0e7ff" stroke="#333" strokeWidth="0.5"/>
              <text x="110" y="60" textAnchor="middle" fontSize="9" fill="#000">Stem Cell Exhaustion</text>
              {/* Altered Intercellular Communication */}
              <path d="M 140 40 A 180 180 0 0 1 200 20 L 200 200 Z" fill="#f3f4f6" stroke="#333" strokeWidth="0.5"/>
              <text x="170" y="35" textAnchor="middle" fontSize="9" fill="#000">Altered Communication</text>
              
              {/* Connecting arrows showing interconnectedness */}
              <defs><marker id="arrowhead" markerWidth="5" markerHeight="3.5" refX="0" refY="1.75" orient="auto"><polygon points="0 0, 5 1.75, 0 3.5" fill="#555"/></marker></defs>
              <line x1="200" y1="20" x2="324.8" y2="75.2" stroke="#555" strokeWidth="1" markerEnd="url(#arrowhead)"/>
              <line x1="324.8" y1="75.2" x2="380" y2="200" stroke="#555" strokeWidth="1" markerEnd="url(#arrowhead)"/>
              <line x1="380" y1="200" x2="324.8" y2="324.8" stroke="#555" strokeWidth="1" markerEnd="url(#arrowhead)"/>
            </svg>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-sm font-medium text-green-800">
              <strong>Key Takeaway:</strong> Interventions targeting one hallmark can have pleiotropic benefits due to their interconnectedness.
            </p>
          </div>
        </div>
      )
    },

    // Slide 5: Expanding the Longevity Toolkit
    {
      title: "Repurposed Drugs & Novel Pharmaceuticals",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-semibold text-blue-800 mb-2">Drug Repurposing (Repositioning)</h4>
                <div className="space-y-2 text-sm">
                  <p>Identifying new uses for already approved drugs.</p>
                  <div className="bg-white p-2 rounded">
                    <strong>Advantages:</strong> Established safety profiles, pharmacokinetic data, accelerated translation, reduced development costs.
                  </div>
                  <div className="bg-white p-2 rounded">
                    <strong>Examples:</strong> Metformin, Rapamycin.
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-semibold text-green-800 mb-2">Novel Pharmaceuticals</h4>
                <div className="space-y-2 text-sm">
                  <p>Specifically designed to target aging pathways.</p>
                  <div className="bg-white p-2 rounded">
                    <strong>Examples:</strong> New senolytics, mitochondrial enhancers, peptide therapies.
                  </div>
                  <div className="bg-white p-2 rounded">
                    Require rigorous preclinical and clinical testing for safety and efficacy.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-800 mb-3">Clinician's Role:</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-2 rounded">
                Critical, evidence-based approach.
              </div>
              <div className="bg-white p-2 rounded">
                Evaluate literature, understand mechanisms, interpret biomarkers, consider risk-benefit.
              </div>
              <div className="bg-white p-2 rounded">
                Temper excitement with commitment to science and patient safety, especially in healthy individuals.
              </div>
            </div>
          </div>

          <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-center">
            <p className="text-sm font-medium text-red-800">
              <strong>Key Takeaway:</strong> The longevity toolkit is growing, demanding careful clinical evaluation and application.
            </p>
          </div>
        </div>
      )
    },

    // Slide 6: mTOR Pathway
    {
      title: "Nutrient-Sensing: mTOR Pathway (mTORC1/mTORC2)",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <h4 className="font-semibold text-blue-800 mb-2">Role in Aging</h4>
              <p className="text-sm">Central kinase integrating nutrient/growth signals; regulates cell growth, proliferation, protein synthesis, autophagy.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-3 rounded-lg">
                <h5 className="font-semibold text-red-800 mb-1">mTORC1</h5>
                <p className="text-sm">Rapamycin-sensitive. Hyperactivation linked to suppressed autophagy, ↑protein synthesis, senescence, shortened lifespan.</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <h5 className="font-semibold text-orange-800 mb-1">mTORC2</h5>
                <p className="text-sm">Regulates cell survival, metabolism. Chronic rapamycin can inhibit; linked to some metabolic side effects.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Diagnostic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-green-50 p-2 rounded">
                  <strong>Biomarkers:</strong> pS6K1, p4E-BP1 in PBMCs (mTORC1 activity), DNA methylation aging clocks
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Clinical Signs:</strong> Subtle; may include features of metabolic syndrome, ↑inflammation
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Therapeutic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-purple-50 p-2 rounded">
                  <strong>Pharmacological:</strong> Rapamycin (Sirolimus) & rapalogs (mTORC1). Indirect: Metformin, Berberine, Curcumin
                </div>
                <div className="bg-yellow-50 p-2 rounded">
                  <strong>Lifestyle:</strong> Caloric restriction, protein restriction, time-restricted eating
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <strong>Interactions:</strong> Autophagy (↓), AMPK (reciprocal inhibition), IIS (activates), SASP (regulates)
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 400 150" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md">
              <style>{`.label { font-size: 12px; fill: #333; } .arrow { stroke: #333; stroke-width: 1.5; fill: none; marker-end: url(#arrowhead); }`}</style>
              <defs><marker id="arrowhead" markerWidth="5" markerHeight="3.5" refX="0" refY="1.75" orient="auto"><polygon points="0 0, 5 1.75, 0 3.5" fill="#333"/></marker></defs>
              <text x="20" y="30" className="label">Nutrients, Growth Factors</text>
              <rect x="150" y="15" width="100" height="30" fill="#f0f0f0" stroke="#333"/>
              <text x="200" y="35" textAnchor="middle" className="label">PI3K/Akt</text>
              <line x1="100" y1="45" x2="150" y2="30" className="arrow"/>
              <rect x="150" y="60" width="100" height="30" fill="#bfdbfe" stroke="#333"/>
              <text x="200" y="80" textAnchor="middle" className="label">mTORC1</text>
              <line x1="200" y1="45" x2="200" y2="60" className="arrow"/>
              <text x="300" y="50" className="label">↑Protein Synthesis</text>
              <text x="300" y="70" className="label">↑Cell Growth</text>
              <text x="300" y="90" className="label">↓Autophagy</text>
              <line x1="250" y1="75" x2="290" y2="50" className="arrow"/>
              <line x1="250" y1="75" x2="290" y2="70" className="arrow"/>
              <line x1="250" y1="75" x2="290" y2="90" className="arrow"/>
              <text x="100" y="120" className="label" fill="red">Rapamycin ---|</text>
              <line x1="180" y1="115" x2="180" y2="90" stroke="red" strokeWidth="1.5"/>
            </svg>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm font-medium text-blue-800">
              <strong>Key Takeaway:</strong> mTORC1 inhibition is a key pro-longevity strategy; balancing efficacy with potential mTORC2-related side effects is crucial.
            </p>
          </div>
        </div>
      )
    },

    // Slide 7: AMPK Pathway
    {
      title: "Nutrient-Sensing: AMPK Pathway",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
              <h4 className="font-semibold text-green-800 mb-2">Role in Aging</h4>
              <p className="text-sm">Cellular energy sensor (activated by ↑AMP:ATP). Promotes catabolism, mitochondrial biogenesis, autophagy. Activity declines with age.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Diagnostic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Biomarkers:</strong> pAMPK (Thr172), pACC in PBMCs/tissue
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <strong>Functional Outcomes:</strong> Improved HOMA-IR, lipid profiles, ↓hs-CRP, ↑mitochondrial function
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Therapeutic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-purple-50 p-2 rounded">
                  <strong>Pharmacological:</strong> Metformin, Berberine, Resveratrol. Investigational: MOTS-c, SLU-PP-332
                </div>
                <div className="bg-yellow-50 p-2 rounded">
                  <strong>Lifestyle:</strong> Exercise, caloric restriction
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <strong>Interactions:</strong> mTORC1 (inhibits), Autophagy (induces), Sirtuins (activates via NAD+), FOXO (activates)
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 400 150" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md">
              <style>{`.label { font-size: 12px; fill: #333; } .arrow { stroke: #333; stroke-width: 1.5; fill: none; marker-end: url(#arrowhead); }`}</style>
              <text x="20" y="30" className="label">Low Energy (↑AMP/ATP)</text>
              <text x="20" y="50" className="label">Exercise, Metformin</text>
              <rect x="150" y="30" width="100" height="30" fill="#bbf7d0" stroke="#333"/>
              <text x="200" y="50" textAnchor="middle" className="label">AMPK</text>
              <line x1="120" y1="40" x2="150" y2="45" className="arrow"/>
              <text x="300" y="20" className="label">↑Mito. Biogenesis</text>
              <text x="300" y="40" className="label">↑Autophagy</text>
              <text x="300" y="60" className="label">↓mTORC1</text>
              <text x="300" y="80" className="label">↑FOXO, ↑SIRT1</text>
              <line x1="250" y1="45" x2="290" y2="20" className="arrow"/>
              <line x1="250" y1="45" x2="290" y2="40" className="arrow"/>
              <line x1="250" y1="45" x2="290" y2="60" className="arrow"/>
              <line x1="250" y1="45" x2="290" y2="80" className="arrow"/>
            </svg>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-sm font-medium text-green-800">
              <strong>Key Takeaway:</strong> AMPK activation is a central mechanism for metabolic health and longevity, often targeted by lifestyle and pharmacological interventions.
            </p>
          </div>
        </div>
      )
    },

    // Slide 8: Insulin/IGF-1 Signaling
    {
      title: "Nutrient-Sensing: Insulin/IGF-1 Signaling (IIS)",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <h4 className="font-semibold text-blue-800 mb-2">Role in Aging</h4>
              <p className="text-sm">Regulates growth, metabolism, lifespan. Reduced IIS linked to lifespan extension in model organisms. Chronic hyperactivation accelerates aging.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Diagnostic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-red-50 p-2 rounded">
                  <strong>Biomarkers:</strong> Fasting glucose/insulin, HbA1c, HOMA-IR, C-peptide, IGF-1, IGFBP-1, IGFBP-3, FOXO3A genetic variants
                </div>
                <div className="bg-orange-50 p-2 rounded">
                  <strong>Clinical Signs:</strong> Central adiposity, acanthosis nigricans
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Therapeutic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-purple-50 p-2 rounded">
                  <strong>Pharmacological:</strong> Metformin (improves insulin sensitivity, can ↓IGF-1). GH secretagogues (e.g., Tesamorelin, CJC-1295/Ipamorelin) - use with caution
                </div>
                <div className="bg-yellow-50 p-2 rounded">
                  <strong>Lifestyle:</strong> Caloric restriction, low glycemic load diets, protein moderation, exercise
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2">Complexity:</h4>
            <p className="text-sm">Mammalian IIS is nuanced. Systemic IGF-1 reduction vs. local tissue needs. Balance GH/IGF-1/insulin. Avoid sarcopenia, bone density loss.</p>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm font-medium text-blue-800">
              <strong>Key Takeaway:</strong> Modulating IIS for longevity requires careful balance to avoid adverse effects on essential anabolic functions. Personalization is key.
            </p>
          </div>
        </div>
      )
    },

    // Slide 9: Mitochondrial Integrity
    {
      title: "Powering Healthspan: Mitochondrial Integrity",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <h4 className="font-semibold text-red-800 mb-2">Role in Aging</h4>
              <p className="text-sm">Mitochondrial dysfunction is a core hallmark. Leads to ↓ATP, ↑ROS, accumulation of damaged mitochondria.</p>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-gray-800">Key Processes:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <h5 className="font-semibold text-blue-800">Biogenesis</h5>
                  <p className="text-xs">Creation of new mitochondria (PGC-1α)</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <h5 className="font-semibold text-green-800">Mitophagy</h5>
                  <p className="text-xs">Selective removal of damaged mitochondria (PINK1/Parkin)</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <h5 className="font-semibold text-purple-800">Bioenergetics</h5>
                  <p className="text-xs">Efficient ATP production</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Diagnostic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Biomarkers:</strong> Mitochondrial respiration (Seahorse), mtDNA copy number/damage, mitophagy markers (LC3-II/I)
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <strong>Oxidative Stress:</strong> 8-OHdG, F2-isoprostanes, plasma acylcarnitines, circulating cell-free mtDNA
                </div>
                <div className="bg-yellow-50 p-2 rounded">
                  <strong>Functional Tests:</strong> VO2 max, muscle strength, cognitive tests
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Therapeutic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-purple-50 p-2 rounded">
                  <strong>Pharmacological:</strong> Urolithin A (mitophagy), SS-31/Elamipretide (stabilizes inner membrane), MOTS-c, BAM-15, Metformin, Berberine, NAD+ precursors
                </div>
                <div className="bg-orange-50 p-2 rounded">
                  <strong>Lifestyle:</strong> Exercise (endurance/resistance), caloric restriction, ketogenic diets
                </div>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-sm font-medium text-green-800">
              <strong>Key Takeaway:</strong> A multi-pronged approach supporting biogenesis, mitophagy, and ROS reduction is vital for mitochondrial health.
            </p>
          </div>
        </div>
      )
    },

    // Slide 10: Cellular Senescence and SASP
    {
      title: "Cellular Senescence: The \"Zombie Cell\" Problem",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <h4 className="font-semibold text-red-800 mb-2">Role in Aging</h4>
              <p className="text-sm">Irreversible cell cycle arrest triggered by stressors. Accumulation of senescent cells contributes to tissue dysfunction via the Senescence-Associated Secretory Phenotype (SASP).</p>
            </div>

            <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
              <h4 className="font-semibold text-orange-800 mb-2">SASP</h4>
              <p className="text-sm">Pro-inflammatory cytokines, chemokines, proteases (e.g., IL-6, IL-8, MMPs) that damage tissue and induce senescence in nearby cells.</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
              <h4 className="font-semibold text-yellow-800 mb-2">Key Pathways</h4>
              <p className="text-sm">p53 (initiation), p16INK4a/Rb (maintenance). p16INK4a is a robust aging biomarker.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Diagnostic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-red-50 p-2 rounded">
                  <strong>Biomarkers:</strong> ↑p16INK4a (T-cells, skin), p21, SA-β-galactosidase, SASP factors (IL-6, IL-8, MMPs), DPP4/CD26
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Therapeutic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-red-50 p-2 rounded">
                  <strong>Senolytics:</strong> Selectively eliminate senescent cells (e.g., Dasatinib + Quercetin (D+Q), Fisetin)
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Senomorphics:</strong> Suppress SASP without killing cells (e.g., Rapamycin, Metformin). Azithromycin may have senolytic/senomorphic properties
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <strong>Strategy:</strong> Intermittent "hit-and-run" senolytic therapy to clear cells, allow tissue repair, minimize side effects
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm font-medium text-blue-800">
              <strong>Key Takeaway:</strong> Targeting senescent cells or their harmful SASP is a promising strategy to combat age-related dysfunction.
            </p>
          </div>
        </div>
      )
    },

    // Slide 11: Inflammation & Immune Dysregulation
    {
      title: "Inflammaging & Immunosenescence: Key Targets",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-semibold text-red-800 mb-2">NF-κB Pathway</h4>
                <div className="space-y-2 text-sm">
                  <p>Master regulator of inflammation; activity increases with age ("inflammaging").</p>
                  <p>Drives SASP.</p>
                  <div className="bg-white p-2 rounded">
                    <strong>Therapeutic Levers:</strong> Curcumin, resveratrol, quercetin, berberine; anti-inflammatory lifestyle.
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-semibold text-blue-800 mb-2">JAK/STAT Pathway</h4>
                <div className="space-y-2 text-sm">
                  <p>Primary signaling for cytokines; critical for immunity, inflammation. Dysregulation contributes to immunosenescence, inflammaging, frailty.</p>
                  <p>Key regulator of SASP production.</p>
                  <div className="bg-white p-2 rounded">
                    <strong>Therapeutic Levers:</strong> JAK inhibitors (e.g., Ruxolitinib) explored as geroprotectors.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-800 mb-3">Clinical Relevance:</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-2 rounded">
                <strong>Biomarkers:</strong> hs-CRP, ESR, pro-inflammatory cytokines (TNF-α, IL-6, IL-1β), pSTAT levels in immune cells
              </div>
              <div className="bg-white p-2 rounded">
                Chronic inflammation (NF-κB) and SASP (JAK/STAT) create a vicious cycle accelerating aging
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-sm font-medium text-green-800">
              <strong>Key Takeaway:</strong> Modulating chronic inflammation and immune dysfunction through pathways like NF-κB and JAK/STAT is crucial for healthy aging.
            </p>
          </div>
        </div>
      )
    },

    // Slide 12: Sirtuins & NAD+ Metabolism
    {
      title: "Sirtuins, NAD+ Metabolism, and DNA Repair",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
              <h4 className="font-semibold text-purple-800 mb-2">Role in Aging</h4>
              <div className="space-y-2 text-sm">
                <p><strong>Sirtuins (SIRT1-7):</strong> NAD+-dependent deacetylases crucial for healthspan (metabolism, stress resistance, DNA repair, inflammation).</p>
                <p><strong>NAD+:</strong> Essential coenzyme for sirtuins; levels decline with age due to ↑consumption (PARPs, CD38) and ↓recycling (NAMPT).</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Diagnostic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Biomarkers:</strong> NAD+/metabolites (NMN, NR, NAM) in blood/tissue; sirtuin activity assays; DNA damage markers (γH2AX, 8-OHdG). NAAD as potential NAD+ increase marker
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Therapeutic Levers:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-green-50 p-2 rounded">
                  <strong>Pharmacological:</strong> NAD+ precursors (NMN, NR). Sirtuin activators (Resveratrol - debated). CD38 inhibitors, NAMPT activators (investigational)
                </div>
                <div className="bg-yellow-50 p-2 rounded">
                  <strong>Lifestyle:</strong> Caloric restriction, exercise
                </div>
                <div className="bg-purple-50 p-2 rounded">
                  <strong>Strategy:</strong> Restoring NAD+ is key, but addressing causes of decline may be more effective long-term
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-lg">
              <style>{`.label { font-size: 10px; fill: #333; } .arrow { stroke: #333; stroke-width: 1; fill: none; marker-end: url(#arrowhead); } .nad { font-weight: bold; fill: #0056b3; } .sirt { fill: #28a745; }`}</style>
              <text x="150" y="30" className="label nad">NAD+</text>
              <text x="250" y="30" className="label nad">NADH</text>
              <line x1="175" y1="30" x2="225" y2="30" className="arrow"/>
              <line x1="225" y1="35" x2="175" y2="35" className="arrow"/>
              <text x="150" y="70" className="label sirt">SIRT1-7</text>
              <line x1="160" y1="40" x2="160" y2="60" className="arrow"/>
              <text x="180" y="70" className="label">(Deacetylation)</text>
              <text x="150" y="100" className="label">Cellular Health, DNA Repair, Metabolism</text>
              <line x1="160" y1="80" x2="160" y2="95" className="arrow"/>
              <text x="30" y="140" className="label">NR/NMN (Precursors)</text>
              <line x1="80" y1="130" x2="130" y2="110" className="arrow"/>
              <text x="150" y="120" className="label nad">NAMPT</text>
              <text x="150" y="140" className="label">NAM (Salvage Pathway)</text>
              <line x1="160" y1="110" x2="160" y2="40" className="arrow"/>
              <line x1="160" y1="130" x2="160" y2="115" className="arrow"/>
              <text x="250" y="70" className="label">CD38 / PARPs</text>
              <text x="250" y="90" className="label">(NAD+ Consumption)</text>
              <line x1="180" y1="40" x2="240" y2="65" className="arrow"/>
            </svg>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm font-medium text-blue-800">
              <strong>Key Takeaway:</strong> Maintaining NAD+ levels is critical for sirtuin activity and overall cellular health; strategies involve precursor supplementation and addressing NAD+ depletion.
            </p>
          </div>
        </div>
      )
    },

    // Slide 13: Introduction to Interventions
    {
      title: "Key Pharmaceutical Interventions for Longevity",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm">Building on our understanding of aging pathways, we can now explore specific interventions. Focus on agents with human data or strong preclinical evidence.</p>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800">Categories to be Discussed:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
                  <p className="text-sm font-medium">mTOR Modulators (Rapamycin)</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-400">
                  <p className="text-sm font-medium">AMPK Activators (Metformin)</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-purple-50 p-3 rounded-lg border-l-4 border-purple-400">
                  <p className="text-sm font-medium">Senolytics (Dasatinib + Quercetin, Fisetin)</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
                  <p className="text-sm font-medium">NAD+ Precursors (NR, NMN)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-800 mb-3">Clinical Considerations for All:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2 text-sm">
                <div className="bg-white p-2 rounded">Mechanism of Action</div>
                <div className="bg-white p-2 rounded">Biomarkers for Assessment & Monitoring</div>
                <div className="bg-white p-2 rounded">Dosing Strategies & Potential Combinations</div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="bg-white p-2 rounded">Evidence from Key Studies (including critiques)</div>
                <div className="bg-white p-2 rounded">Safety and Side Effect Profile</div>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-sm font-medium text-green-800">
              <strong>Key Takeaway:</strong> We will now delve into specific drugs, emphasizing clinical application and evidence.
            </p>
          </div>
        </div>
      )
    },

    // Slide 14: Rapamycin Mechanism & Clinical Relevance
    {
      title: "mTOR Modulator: Rapamycin (Sirolimus)",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <h4 className="font-semibold text-blue-800 mb-2">Mechanism</h4>
              <div className="space-y-2 text-sm">
                <p>FDA-approved macrolide; primarily inhibits mTORC1 by forming complex with FKBP12.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="bg-white p-2 rounded">Suppresses protein synthesis</div>
                  <div className="bg-white p-2 rounded">Induces autophagy</div>
                  <div className="bg-white p-2 rounded">Modulates cellular senescence & SASP</div>
                  <div className="bg-white p-2 rounded">Cancer suppression (lifespan extension in mice)</div>
                </div>
                <p className="text-red-600"><strong>Note:</strong> Chronic high doses may inhibit mTORC2 (potential metabolic side effects).</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3">Clinically Relevant Applications:</h4>
            <p className="text-sm"><strong>Potential Benefits (Research Areas):</strong> Improved immune function, reduced infections, decreased senescent cell markers, enhanced cardiac function, neuroprotection.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Biomarkers for Assessment & Monitoring:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-green-50 p-2 rounded">
                  <strong>Efficacy:</strong> ↓pS6K1, ↓p4E-BP1 (PBMCs). Improved immune markers (↑vaccine response, ↓PD1+ T-cells). ↓p16INK4a
                </div>
                <div className="bg-red-50 p-2 rounded">
                  <strong>Safety:</strong> Lipids (TG, LDL, HDL), glucose, insulin, HbA1c, CBC, renal function, LFTs. Monitor for mouth sores, impaired wound healing
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">Clinical Pearl</h4>
                <p className="text-sm">Monitor for aphthous ulcers (mouth sores) - often the first sign of rapamycin effects. Consider dose reduction if persistent.</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm font-medium text-blue-800">
              <strong>Key Takeaway:</strong> Rapamycin is a potent mTORC1 inhibitor with significant healthspan-extending effects in animals; human research is ongoing to optimize dosing and manage side effects.
            </p>
          </div>
        </div>
      )
    },

    // Slide 15: Rapamycin Studies, Dosing & Monitoring
    {
      title: "Rapamycin: Evidence, Dosing, and Clinical Pearls",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800">Key Studies & Findings:</h4>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm"><strong>NIA Interventions Testing Program (ITP):</strong> Consistently extended lifespan in mice, even when started late in life. Attributed partly to cancer delay/suppression.</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm"><strong>Dog Aging Project (TRIAD):</strong> Showed improved cardiac function and activity in aged dogs with intermittent rapamycin.</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm"><strong>PEARL Trial (Kraig et al., 2024):</strong> 1 year, 5-10mg weekly in healthy older adults (n=25 Rapa, n=13 Placebo). Generally safe. Trend towards improved immune profile, some lipid elevations. No significant change in frailty or physical function in this short study.</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2">Critique:</h4>
            <p className="text-sm">Most robust data is in animal models. Human trials are smaller, shorter duration. Optimal dosing (dose, frequency, duration) for longevity in humans is unknown. Heterogeneity in response.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Therapeutic Considerations (Off-Label):</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Dosing:</strong> Typically low-dose, intermittent (e.g., 3-6 mg once weekly). Some use "cycles"
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <strong>Monitoring:</strong> Baseline and regular follow-up (every 3-6 months) of safety biomarkers
                </div>
                <div className="bg-purple-50 p-2 rounded">
                  <strong>Combinations:</strong> With metformin, senolytics (caution, synergistic effects unknown)
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Side Effects:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-red-50 p-2 rounded">
                  Mouth sores (aphthous ulcers)
                </div>
                <div className="bg-orange-50 p-2 rounded">
                  Dyslipidemia
                </div>
                <div className="bg-yellow-50 p-2 rounded">
                  Hyperglycemia/insulin resistance (esp. higher/continuous doses)
                </div>
                <div className="bg-pink-50 p-2 rounded">
                  Impaired wound healing
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  Rare interstitial pneumonitis
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-center">
            <p className="text-sm font-medium text-red-800">
              <strong>Key Takeaway:</strong> While promising, rapamycin use for longevity is investigational. Requires careful patient selection, informed consent, and diligent monitoring.
            </p>
          </div>
        </div>
      )
    },

    // Slide 16: Metformin
    {
      title: "AMPK Activator: Metformin",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
              <h4 className="font-semibold text-green-800 mb-2">Mechanism</h4>
              <p className="text-sm mb-2">Widely used anti-diabetic drug. Multiple proposed mechanisms for longevity:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className="bg-white p-2 rounded">Indirect AMPK activation (primary hypothesis)</div>
                <div className="bg-white p-2 rounded">Inhibition of mitochondrial complex I (mildly)</div>
                <div className="bg-white p-2 rounded">Reduced hepatic gluconeogenesis</div>
                <div className="bg-white p-2 rounded">Modulation of gut microbiome</div>
                <div className="bg-white p-2 rounded">Potential mTORC1 inhibition (downstream of AMPK)</div>
                <div className="bg-white p-2 rounded">Anti-inflammatory effects (↓NF-κB), May reduce IGF-1 levels</div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">Clinically Relevant Applications (Beyond Diabetes):</h4>
            <p className="text-sm"><strong>Potential Benefits:</strong> Improved insulin sensitivity, weight management, reduced cancer risk (some studies), cardiovascular protection.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Biomarkers for Assessment & Monitoring:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-green-50 p-2 rounded">
                  <strong>Efficacy:</strong> Glucose, HbA1c, HOMA-IR, weight/BMI, lipids, hs-CRP
                </div>
                <div className="bg-red-50 p-2 rounded">
                  <strong>Safety:</strong> Renal function (eGFR - contraindicated if {`<`}30 mL/min/1.73m²), Vitamin B12 levels (monitor for deficiency)
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Key Studies:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-purple-50 p-2 rounded">
                  <strong>DPP/DPPOS:</strong> Reduced progression to T2D in prediabetics
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <strong>UKPDS:</strong> Cardiovascular benefits in T2D patients
                </div>
                <div className="bg-yellow-50 p-2 rounded">
                  <strong>Bannister et al. (2014):</strong> T2D patients on metformin lived longer than matched non-diabetic controls (controversial)
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <strong>TAME Trial:</strong> Ongoing landmark trial to assess metformin's effect on delaying age-related diseases in non-diabetics
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2">Therapeutic Considerations (Off-Label for Longevity):</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-2 rounded">
                <strong>Dosing:</strong> 500 mg to 2000 mg daily (often extended-release to minimize GI side effects). Start low, titrate up.
              </div>
              <div className="bg-white p-2 rounded">
                <strong>Side Effects:</strong> GI upset (diarrhea, nausea – often transient), B12 deficiency, lactic acidosis (rare, primarily in renal impairment).
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-sm font-medium text-green-800">
              <strong>Key Takeaway:</strong> Metformin is a safe, inexpensive drug with pleiotropic effects relevant to aging. The TAME trial will provide crucial data for its use in non-diabetics for longevity.
            </p>
          </div>
        </div>
      )
    },

    // Slide 17: Senolytics
    {
      title: "Senolytics: Targeting \"Zombie\" Cells",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <h4 className="font-semibold text-red-800 mb-2">Mechanism</h4>
              <p className="text-sm mb-2">Drugs that selectively induce apoptosis in senescent cells.</p>
              <div className="space-y-2 text-sm">
                <div className="bg-white p-2 rounded">
                  <strong>Dasatinib + Quercetin (D+Q):</strong> Dasatinib (tyrosine kinase inhibitor) + Quercetin (flavonoid). Targets multiple pro-survival pathways in senescent cells.
                </div>
                <div className="bg-white p-2 rounded">
                  <strong>Fisetin:</strong> Natural flavonoid with senolytic properties.
                </div>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-2">Clinically Relevant Applications:</h4>
            <p className="text-sm"><strong>Potential Benefits (Research Areas):</strong> Improve physical function, reduce inflammation, alleviate age-related organ dysfunction (e.g., IPF, CKD, osteoarthritis).</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Biomarkers for Assessment & Monitoring:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Efficacy:</strong> ↓Senescent cell markers (p16INK4a, SA-β-gal), ↓SASP factors (IL-6, MMPs), improved organ-specific function, frailty scores
                </div>
                <div className="bg-red-50 p-2 rounded">
                  <strong>Safety:</strong> D+Q: fluid retention, GI upset, potential myelosuppression. Fisetin: generally well-tolerated
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Key Studies & Findings:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-purple-50 p-2 rounded">
                  <strong>Justice et al. (2019, IPF):</strong> First-in-human D+Q trial in idiopathic pulmonary fibrosis. Improved physical function, reduced SASP
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Hickson et al. (2019, DKD):</strong> D+Q in diabetic kidney disease. Reduced senescent cells in adipose tissue and skin
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <strong>Mayo Clinic Fisetin Trials:</strong> Ongoing studies for various age-related conditions. Animal studies show fisetin extends healthspan and lifespan
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2">Critique:</h4>
            <p className="text-sm">Field is young. Optimal senolytics, dosing regimens ("hit-and-run"), long-term safety, and efficacy in diverse populations are still under investigation. Risk of impairing beneficial transient senescence (e.g., wound healing).</p>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-800 mb-2">Therapeutic Considerations (Investigational):</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-2 rounded">
                <strong>D+Q Dosing:</strong> Dasatinib 100mg + Quercetin 1000-1250mg, orally, for 2-3 consecutive days, repeated every few weeks to months
              </div>
              <div className="bg-white p-2 rounded">
                <strong>Fisetin Dosing:</strong> Typically 20mg/kg/day for 2 consecutive days, repeated monthly (based on animal studies, human doses vary)
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm font-medium text-blue-800">
              <strong>Key Takeaway:</strong> Senolytics offer a novel approach to target a core hallmark of aging. Intermittent dosing is key. Human data is emerging but promising.
            </p>
          </div>
        </div>
      )
    },

    // Slide 18: NAD+ Precursors
    {
      title: "NAD+ Precursors: Nicotinamide Riboside (NR) & Mononucleotide (NMN)",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
              <h4 className="font-semibold text-purple-800 mb-2">Mechanism</h4>
              <p className="text-sm mb-2">Aim to boost cellular NAD+ levels, which decline with age. NAD+ is crucial for:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className="bg-white p-2 rounded">Sirtuin activity (epigenetic regulation, DNA repair, metabolic homeostasis)</div>
                <div className="bg-white p-2 rounded">PARP activity (DNA repair)</div>
                <div className="bg-white p-2 rounded">CD38 activity (calcium signaling, NAD+ consumption)</div>
                <div className="bg-white p-2 rounded">Mitochondrial function (Redox reactions)</div>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-2">Clinically Relevant Applications:</h4>
            <p className="text-sm"><strong>Potential Benefits (Research Areas):</strong> Improved mitochondrial function, enhanced DNA repair, metabolic benefits, neuroprotection, improved physical performance.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Biomarkers for Assessment & Monitoring:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <strong>Efficacy:</strong> Blood/tissue NAD+ levels (and metabolites like NAAD). Functional outcomes (muscle strength, endurance, metabolic markers) – still exploratory
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <strong>Safety:</strong> Generally well-tolerated
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Key Studies & Findings:</h4>
              <div className="space-y-2 text-sm">
                <div className="bg-purple-50 p-2 rounded">
                  <strong>Human Trials (NR & NMN):</strong> Show increased blood NAD+ levels
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  Some studies show modest improvements in muscle function, insulin sensitivity, blood pressure, or inflammatory markers, particularly in older or metabolically compromised individuals
                </div>
                <div className="bg-yellow-50 p-2 rounded">
                  Many studies show no significant clinical benefit on primary endpoints despite NAD+ repletion
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2">Critique:</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-2 rounded">
                Bioavailability and conversion efficiency to NAD+ in target tissues can vary
              </div>
              <div className="bg-white p-2 rounded">
                Optimal dosing and long-term effects are not fully established
              </div>
              <div className="bg-white p-2 rounded">
                Impact on robust healthspan/lifespan endpoints in humans is unproven
              </div>
              <div className="bg-white p-2 rounded">
                Addressing NAD+ consumption (e.g., CD38 inhibition) alongside repletion might be more effective
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-800 mb-2">Therapeutic Considerations (Supplements):</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-2 rounded">
                <strong>Dosing:</strong> NR: 250-1000 mg/day. NMN: 250-1000 mg/day
              </div>
              <div className="bg-white p-2 rounded">
                Often combined with pterostilbene or resveratrol (attempted STACs)
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm font-medium text-blue-800">
              <strong>Key Takeaway:</strong> NAD+ precursors can raise NAD+ levels. Clinical benefits in healthy humans are still being established. A multi-faceted approach to NAD+ homeostasis may be needed.
            </p>
          </div>
        </div>
      )
    },

    // Slide 19: Clinical Application Framework
    {
      title: "Integrating Longevity Medicine into Clinical Practice",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800">Personalized Approach is Key:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
                  <p className="text-sm"><strong>Comprehensive Assessment:</strong> Detailed history, lifestyle, genetics, advanced biomarkers (hallmarks, pathway activity).</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-400">
                  <p className="text-sm"><strong>Risk Stratification:</strong> Identify individuals most likely to benefit.</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-purple-50 p-3 rounded-lg border-l-4 border-purple-400">
                  <p className="text-sm"><strong>Shared Decision-Making:</strong> Discuss evidence, potential benefits, risks, and uncertainties.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-800 mb-3">Testing & Monitoring Protocol (General Principles):</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-2 rounded">
                <strong>Baseline:</strong> Relevant biomarkers for targeted pathways (mTOR, AMPK, IIS, inflammation, senescence, NAD+). Organ function tests.
              </div>
              <div className="bg-white p-2 rounded">
                <strong>Intervention Selection:</strong> Based on individual profile, goals, and evidence. Start with lifestyle foundations.
              </div>
              <div className="bg-white p-2 rounded">
                <strong>Follow-up:</strong> Monitor efficacy (biomarker changes, functional improvements) and safety (side effects, lab parameters) regularly (3-6 month intervals).
              </div>
              <div className="bg-white p-2 rounded">
                <strong>Adaptation:</strong> Adjust interventions based on response and new evidence.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-2">Established:</h4>
              <p className="text-sm">Lifestyle (diet, exercise, sleep, stress management), managing cardiometabolic risk factors.</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800 mb-2">Newer/Investigational:</h4>
              <p className="text-sm">Rapamycin, senolytics, specific peptide therapies, novel NAD+ strategies. Use with caution and strong rationale.</p>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-3">Future Directions:</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-2 rounded">Better biomarkers of biological aging and intervention efficacy</div>
              <div className="bg-white p-2 rounded">More robust human clinical trials for longevity agents</div>
              <div className="bg-white p-2 rounded">Combination therapies targeting multiple hallmarks</div>
              <div className="bg-white p-2 rounded">AI and machine learning for personalized predictions and interventions</div>
            </div>
          </div>

          <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-center">
            <p className="text-sm font-medium text-red-800">
              <strong>Key Takeaway:</strong> Longevity medicine requires a thoughtful, personalized, and evidence-informed approach, continually evolving with research. Prioritize safety and foundational health.
            </p>
          </div>
        </div>
      )
    },

    // Slide 20: SHIFT MACRO-SUMMARY
    {
      title: "\"SHIFT\" MACRO-SUMMARY: Turning Geroscience into a Practical, Monitored Clinical Protocol",
      subtitle: "(Use as single dense landscape slide; print as handout for legibility)",
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">TAKE-HOME PATTERN RECOGNITION</h4>
            <div className="text-xs space-y-1">
              <p>• Every lever has both a biomarker & a monitoring cadence—build these into your EMR flowsheets.</p>
              <p>• Start with lifestyle "one lever per SHIFT pillar," add pharmaceuticals sequentially, reassessing biologic-age clock every 6-12 months.</p>
              <p>• Use LOW-DOSE / INTERMITTENT strategies first; combine where mechanisms are complementary but toxicities non-overlapping.</p>
              <p>• Always obtain informed consent—most uses are off-label, investigational.</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-2 text-left">SHIFT PILLAR</th>
                  <th className="border border-gray-300 p-2 text-left">MAIN AGING TARGETS</th>
                  <th className="border border-gray-300 p-2 text-left">KEY BIOMARKERS</th>
                  <th className="border border-gray-300 p-2 text-left">CORE PHARMACEUTICALS</th>
                  <th className="border border-gray-300 p-2 text-left">LIFESTYLE LEVERS</th>
                  <th className="border border-gray-300 p-2 text-left">MONITORING PEARLS</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-2 bg-blue-50">
                    <strong>S – Sensing & metabolic fuel-gauging</strong>
                  </td>
                  <td className="border border-gray-300 p-2">
                    mTORC1/2, AMPK, IIS (Insulin/IGF-1), NAD⁺/Sirtuins, Mitoch. energetic flexibility
                  </td>
                  <td className="border border-gray-300 p-2">
                    Fasting glucose/insulin, HbA1c, HOMA-IR, IGF-1, pS6K1, pAMPK, NAD⁺/NAAD, Lipid panel, DXA
                  </td>
                  <td className="border border-gray-300 p-2">
                    Rapamycin/rapalogs (mTORC1↓), Metformin, Berberine (AMPK↑), NR, NMN (NAD⁺↑), MOTS-c
                  </td>
                  <td className="border border-gray-300 p-2">
                    Caloric/protein restriction, TRE, Resistance + aerobic exercise, Ketogenic or low-GL diet
                  </td>
                  <td className="border border-gray-300 p-2">
                    Start Rapa 3-6 mg q7-14d; check TG, LDL, oral ulcers. eGFR{'>'}45 for metformin; watch B12
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 bg-green-50">
                    <strong>H – Homeostatic defence & repair</strong>
                  </td>
                  <td className="border border-gray-300 p-2">
                    Autophagy/Mitophagy (ULK1, PINK1/Parkin), Proteostasis, DNA repair, Stem-cell niche
                  </td>
                  <td className="border border-gray-300 p-2">
                    LC3-II/I, p62, mtDNA copy #, 8-OHdG, γ-H2AX, GDF-15, VO2-max, Telomere length
                  </td>
                  <td className="border border-gray-300 p-2">
                    Rapamycin (autophagy↑), Urolithin-A (mitophagy↑), SS-31/Elamipretide, NAD⁺ precursors, Thymosin-α1
                  </td>
                  <td className="border border-gray-300 p-2">
                    Zone-2 + HIIT exercise, Sauna/cold hormesis, Adequate protein (1.2 g/kg), Sleep hygiene
                  </td>
                  <td className="border border-gray-300 p-2">
                    Cycle autophagy inducers; re-feed days. Monitor AST/ALT with Tesamorelin; IGF-1 150-250 ng/mL target
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 bg-red-50">
                    <strong>I – Inflammation & immune tone</strong>
                  </td>
                  <td className="border border-gray-300 p-2">
                    NF-κB, JAK/STAT, SASP burden, Immunosenescence markers
                  </td>
                  <td className="border border-gray-300 p-2">
                    hs-CRP, IL-6, TNF-α, pSTAT3, p16INK4a, Vaccine Ab titres, Neutrophil:lymphocyte ratio
                  </td>
                  <td className="border border-gray-300 p-2">
                    Senolytics: Dasatinib 100mg + Quercetin 1g x2-3d/mo; Fisetin 20mg/kg, Rapamycin, Curcumin/Resveratrol
                  </td>
                  <td className="border border-gray-300 p-2">
                    Mediterranean/low-AGE diet, ω-3 EPA-DHA, Mind-body (HRV↑), Oral & gut microbiome care
                  </td>
                  <td className="border border-gray-300 p-2">
                    "Hit-and-run" senolytic pulses; CBC, LFTs pre-/post-cycle. Hold D+Q peri-surgery. Track CRP drop ≥30%
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 bg-yellow-50">
                    <strong>F – Fibrosis & structural remodelling</strong>
                  </td>
                  <td className="border border-gray-300 p-2">
                    TGF-β/Smad, RAAS/Ang II-AT₁, ECM turnover, Advanced glycation
                  </td>
                  <td className="border border-gray-300 p-2">
                    TGF-β1, Galectin-3, PIIINP, NT-proBNP, eGFR, UACR, FibroScan kPa
                  </td>
                  <td className="border border-gray-300 p-2">
                    ACE-i/ARB (Ang II↓), Spironolactone, Metformin (AGE↓), Nrf2 activators (Sulforaphane), Senolytics adjunct
                  </td>
                  <td className="border border-gray-300 p-2">
                    Glycaemic control, low-fructose, Regular resistance exercise, Vit D 40-50 ng/mL, Avoid smoking
                  </td>
                  <td className="border border-gray-300 p-2">
                    Check K⁺ & creatinine 2-4 wks after RAAS start. Combine senolytics + ACE-i for early CKD/IPF
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-2 bg-purple-50">
                    <strong>T – Time-keeping & chronobiology</strong>
                  </td>
                  <td className="border border-gray-300 p-2">
                    Central & peripheral clocks (CLOCK/BMAL1), Melatonin, SIRT1-NAMPT-NAD⁺ loop
                  </td>
                  <td className="border border-gray-300 p-2">
                    Salivary cortisol & melatonin curves, BMAL1 mRNA, Actigraphy, HRV circadian amplitude
                  </td>
                  <td className="border border-gray-300 p-2">
                    Melatonin 0.3-3mg pre-bed, NR/NMN (SIRT1-CLOCK sync), Time-scheduled Rapamycin/Metformin
                  </td>
                  <td className="border border-gray-300 p-2">
                    Fixed sleep-wake ±30min, Early-day TRF (8-10h), Morning outdoor light, Blue-light block 1h pre-bed
                  </td>
                  <td className="border border-gray-300 p-2">
                    Re-check actigraphy quarterly until SE {'>'}85%. Dose melatonin lowest effective. Coordinate drug timing
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },

    // Slide 21: References
    {
      title: "Selected References",
      content: (
        <div className="space-y-4">
          <div className="text-xs space-y-2">
            <div className="bg-gray-50 p-2 rounded">
              <p>Bartke A, Brown-Borg H. Growth hormone and aging: a clinical review. PMC. 2024.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Jones DS, Hofmann L. Functional Medicine: An Operating System for Integrative and Lifestyle Medicine. Integr Med (Encinitas). 2016.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Hickson LJ, Langhi Prata LGP, Bobart SA, et al. Senolytics decrease senescent cells in humans: Preliminary report from a clinical trial of Dasatinib plus Quercetin in individuals with diabetic kidney disease. EBioMedicine. 2019.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>López-Otín C, Blasco MA, Partridge L, Serrano M, Kroemer G. The hallmarks of aging. Cell. 2013. & López-Otín C, et al. Hallmarks of aging: An expanding universe. Cell. 2023.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Kaeberlein M, et al. Rapamycin fed late in life extends lifespan in genetically heterogeneous mice. Nature. 2009.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Kraig E, et al. Influence of rapamycin on safety and healthspan metrics after one year: PEARL trial results. GeroScience. 2024.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Barzilai N, Crandall JP, Kritchevsky SB, Espeland MA. Metformin as a Tool to Target Aging. Cell Metab. 2016.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Justice JN, Nambiar AM, Tchkonia T, et al. Senolytics in idiopathic pulmonary fibrosis: Results from a first-in-human, open-label, pilot study. EBioMedicine. 2019.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Martens CR, Denman BA, Mazzo MR, et al. Chronic nicotinamide riboside supplementation is well-tolerated and elevates NAD+ in healthy middle-aged and older adults. Nat Commun. 2018.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Imai S, Guarente L. NAD+ and sirtuins in aging and disease. Trends Cell Biol. 2014.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Sae-Lee C, et al. mTOR signaling in growth control and disease. Cell. 2012.</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p>Salminen A, Kaarniranta K. AMP-activated protein kinase (AMPK) and aging. J Mol Med (Berl). 2009.</p>
            </div>
          </div>
        </div>
      )
    },

    // Slide 22: Q&A
    {
      title: "Thank You & Questions",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <h3 className="text-lg font-bold text-blue-800 mb-4">Questions & Discussion</h3>
            <p className="text-sm text-gray-700">
              Thank you for your attention. We welcome your questions about implementing pharmaceuticals and repurposed medications for longevity in clinical practice.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">Key Discussion Points:</h4>
              <div className="space-y-2 text-sm">
                <p>• Clinical implementation strategies</p>
                <p>• Risk-benefit considerations</p>
                <p>• Patient selection criteria</p>
                <p>• Monitoring protocols</p>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-2">Areas for Future Research:</h4>
              <div className="space-y-2 text-sm">
                <p>• Optimal dosing strategies</p>
                <p>• Combination therapies</p>
                <p>• Biomarker development</p>
                <p>• Long-term safety data</p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <Link href="/" className="flex items-center hover:text-blue-600 transition-colors">
              <Home className="h-4 w-4 mr-1" />
              Home
            </Link>
            <span>/</span>
            <Link href="/mentorship" className="flex items-center hover:text-blue-600 transition-colors">
              <Book className="h-4 w-4 mr-1" />
              Mentorship
            </Link>
            <span>/</span>
            <span className="text-blue-600 font-medium">Pharmaceuticals and Repurposed Medications</span>
          </nav>
        </div>

        {/* Slide Container */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-sm font-medium text-gray-500">
              Slide {currentSlide + 1} of {slides.length}
            </h2>
            <div className="text-sm text-gray-500">
              Pharmaceuticals and Repurposed Medications for Longevity
            </div>
          </div>

          <div className="min-h-[600px]">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {slides[currentSlide].title}
              </h1>
              {slides[currentSlide].subtitle && (
                <p className="text-lg text-gray-600 mb-4">
                  {slides[currentSlide].subtitle}
                </p>
              )}
            </div>
            
            <div className="prose prose-sm max-w-none">
              {slides[currentSlide].content}
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            Previous
          </button>

          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
          >
            Next
            <ChevronRight className="h-5 w-5 ml-2" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mt-8">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            ></div>
          </div>
          <p className="text-center text-sm text-gray-600 mt-2">
            Progress: {Math.round(((currentSlide + 1) / slides.length) * 100)}%
          </p>
        </div>

        {/* Accompanying Audio Embed */}
        <div className="mt-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Accompanying Audio</h3>
            <div className="flex justify-center">
              <iframe 
                src="https://share.descript.com/embed/wEqXPqFrAIz" 
                width="640" 
                height="360" 
                frameBorder="0" 
                allowFullScreen
                className="rounded-lg shadow-md"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}